import React, { useState, useEffect } from 'react';
import { useCourseCreate, useCourseUpdate } from '@/hooks/mutations/course';
import { useMentorList } from '@/hooks/queries/mentor/useList';
import { Course, CourseCreateInput, CourseFormat, CourseDifficulty, CourseStatus, PricingType, EntryLevel, PaymentFormat } from '@/types/courses';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MarkdownEditor } from '@/components/fields';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/components/ui/use-toast';
import { FileUploadInput } from '@/components/FileUploadInput';
import { AlertDialog } from '@/components/AlertDialog';

interface CourseModalProps {
  isOpen: boolean;
  onClose: () => void;
  courseToEdit?: Course | null;
  onSuccess?: () => void;
}

const defaultTranslations = [
  { locale: 'uz', name: '', title: '', subtitle: '', description: '', courseOutcomes: '', direction: '', profession: '' },
  { locale: 'ru', name: '', title: '', subtitle: '', description: '', courseOutcomes: '', direction: '', profession: '' },
  { locale: 'en', name: '', title: '', subtitle: '', description: '', courseOutcomes: '', direction: '', profession: '' },
];

export const CourseModal: React.FC<CourseModalProps> = ({
  isOpen,
  onClose,
  courseToEdit,
  onSuccess,
}) => {
  const { toast } = useToast();
  const { data: mentorsData } = useMentorList({ pageSize: 100 });
  const mentors: any[] = mentorsData?.data?.data || mentorsData?.data || [];

  const { mutate: createCourse, isPending: isCreating } = useCourseCreate();
  const { mutate: updateCourse, isPending: isUpdating } = useCourseUpdate();

  const [publishConfirmOpen, setPublishConfirmOpen] = useState(false);
  const [pendingPayload, setPendingPayload] = useState<CourseCreateInput | null>(null);

  const [formData, setFormData] = useState<CourseCreateInput>({
    format: 'COURSE',
    photo: '',
    mentorId: '',
    icon: '',
    bannerImage: '',
    cardImage: '',
    decorImage: '',
    difficulty: 'BEGINNER',
    duration: 0,
    price: 0,
    status: 'DRAFT',
    pricingType: 'FREE',
    entryLevel: 'BEGINNER',
    paymentFormat: 'FULL',
    publishDate: new Date().toISOString(),
    presalesEnabled: false,
    waitlistEnabled: false,
    translations: defaultTranslations,
  });

  useEffect(() => {
    if (courseToEdit) {
      setFormData({
        format: courseToEdit.format || 'COURSE',
        photo: courseToEdit.photo || '',
        mentorId: String(courseToEdit.mentorId || ''),
        icon: courseToEdit.icon || '',
        bannerImage: (courseToEdit as any).bannerImage || (courseToEdit as any).banner || '',
        cardImage: courseToEdit.cardImage || '',
        decorImage: courseToEdit.decorImage || '',
        difficulty: courseToEdit.difficulty || 'BEGINNER',
        duration: courseToEdit.duration || 0,
        price: courseToEdit.price || 0,
        status: courseToEdit.status || 'DRAFT',
        pricingType: courseToEdit.pricingType || 'FREE',
        entryLevel: courseToEdit.entryLevel || 'BEGINNER',
        paymentFormat: courseToEdit.paymentFormat || 'FULL',
        publishDate: courseToEdit.publishDate || new Date().toISOString(),
        presalesEnabled: !!courseToEdit.presalesEnabled,
        waitlistEnabled: !!courseToEdit.waitlistEnabled,
        translations: [
          {
            locale: 'uz',
            name: courseToEdit.name || '',
            title: courseToEdit.title || '',
            subtitle: courseToEdit.subtitle || '',
            description: courseToEdit.description || '',
            courseOutcomes: courseToEdit.courseOutcomes || '',
            direction: courseToEdit.direction || '',
            profession: courseToEdit.profession || '',
          },
          {
            locale: 'ru',
            name: courseToEdit.nameRu || '',
            title: courseToEdit.titleRu || '',
            subtitle: courseToEdit.subtitleRu || '',
            description: courseToEdit.descriptionRu || '',
            courseOutcomes: courseToEdit.courseOutcomesRu || '',
            direction: courseToEdit.directionRu || '',
            profession: courseToEdit.professionRu || '',
          },
          {
            locale: 'en',
            name: '',
            title: '',
            subtitle: '',
            description: '',
            courseOutcomes: '',
            direction: '',
            profession: '',
          },
        ],
      });
    } else {
      setFormData({
        format: 'COURSE',
        photo: '',
        mentorId: '',
        icon: '',
        bannerImage: '',
        cardImage: '',
        decorImage: '',
        difficulty: 'BEGINNER',
        duration: 0,
        price: 0,
        status: 'DRAFT',
        pricingType: 'FREE',
        entryLevel: 'BEGINNER',
        paymentFormat: 'FULL',
        publishDate: new Date().toISOString(),
        presalesEnabled: false,
        waitlistEnabled: false,
        translations: defaultTranslations,
      });
    }
  }, [courseToEdit, isOpen]);

  const handleTranslationChange = (
    locale: string,
    field: string,
    value: string
  ) => {
    setFormData((prev) => ({
      ...prev,
      translations: prev.translations.map((t) =>
        t.locale === locale ? { ...t, [field]: value } : t
      ),
    }));
  };

  const buildPayload = (): CourseCreateInput => {
    const { presalesEnabled: _ignored, ...rest } = formData;
    return {
      ...rest,
      duration: Number(formData.duration) || 0,
      price: Number(formData.price) || 0,
    };
  };

  const submitUpdate = (payload: CourseCreateInput) => {
    if (!courseToEdit) return;
    updateCourse(
      { id: courseToEdit.id, data: payload },
      {
        onSuccess: () => {
          toast({ title: 'Muvaffaqiyatli', description: 'Kurs yangilandi' });
          onClose();
          onSuccess?.();
        },
        onError: (err: any) => {
          toast({
            title: 'Xatolik',
            description: err?.response?.data?.message || 'Kursni yangilab bo\'lmadi',
            variant: 'destructive',
          });
        },
      }
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload = buildPayload();

    if (courseToEdit) {
      const wasPresales = courseToEdit.status === 'PRE_SALES';
      const publishing = wasPresales && payload.status === 'PUBLISHED';
      if (publishing) {
        setPendingPayload(payload);
        setPublishConfirmOpen(true);
        return;
      }
      submitUpdate(payload);
    } else {
      createCourse(payload, {
        onSuccess: () => {
          toast({ title: 'Muvaffaqiyatli', description: 'Yangi kurs yaratildi' });
          onClose();
          onSuccess?.();
        },
        onError: (err: any) => {
          toast({
            title: 'Xatolik',
            description: err?.response?.data?.message || 'Kursni yaratib bo\'lmadi',
            variant: 'destructive',
          });
        },
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white rounded-2xl p-6 custom-scrollbar">
        <DialogHeader className="pr-12">
          <DialogTitle className="text-xl font-bold text-slate-900">
            {courseToEdit ? 'Kursni tahrirlash' : 'Yangi kurs yaratish'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          {/* Asosiy Sozlamalar */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-slate-50 p-4 rounded-xl border border-slate-100">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-700">Format</Label>
              <select
                className="w-full h-10 px-3 rounded-xl border border-input bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                value={formData.format}
                onChange={(e) => setFormData({ ...formData, format: e.target.value as CourseFormat })}
              >
                <option value="COURSE">COURSE (Standart kurs)</option>
                <option value="SKILL">SKILL (Ko'nikma darsi)</option>
                <option value="PROFESSION">PROFESSION (Kasb/Bootcamp)</option>
              </select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-700">Qiyinlik darajasi (Difficulty)</Label>
              <select
                className="w-full h-10 px-3 rounded-xl border border-input bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                value={formData.difficulty}
                onChange={(e) => setFormData({ ...formData, difficulty: e.target.value as CourseDifficulty })}
              >
                <option value="BEGINNER">BEGINNER (Boshlang'ich)</option>
                <option value="INTERMEDIATE">INTERMEDIATE (O'rta)</option>
                <option value="ADVANCED">ADVANCED (Yuqori/Murakkab)</option>
              </select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-700">Kirish darajasi (Entry Level)</Label>
              <select
                className="w-full h-10 px-3 rounded-xl border border-input bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                value={formData.entryLevel}
                onChange={(e) => setFormData({ ...formData, entryLevel: e.target.value as EntryLevel })}
              >
                <option value="BEGINNER">BEGINNER (Boshlang'ich)</option>
                <option value="INTERMEDIATE">INTERMEDIATE (O'rta)</option>
                <option value="ADVANCED">ADVANCED (Yuqori)</option>
              </select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-700">Holat (Status)</Label>
              <select
                className="w-full h-10 px-3 rounded-xl border border-input bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value as CourseStatus })}
              >
                <option value="DRAFT">DRAFT (Qoralama)</option>
                {(courseToEdit?.status === 'PRE_SALES' || formData.status === 'PRE_SALES') && (
                  <option value="PRE_SALES">PRE_SALES (Oldindan sotuv)</option>
                )}
                <option value="PUBLISHED">PUBLISHED (Faol/Chop etilgan)</option>
                <option value="ARCHIVED">ARCHIVED (Arxivlangan)</option>
              </select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-700">Narx turi (Pricing Type)</Label>
              <select
                className="w-full h-10 px-3 rounded-xl border border-input bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                value={formData.pricingType}
                onChange={(e) => setFormData({ ...formData, pricingType: e.target.value as PricingType })}
              >
                <option value="FREE">FREE (Bepul)</option>
                <option value="PAID">PAID (Pullik)</option>
              </select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-700">To'lov formati (Payment Format)</Label>
              <select
                className="w-full h-10 px-3 rounded-xl border border-input bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                value={formData.paymentFormat}
                onChange={(e) => setFormData({ ...formData, paymentFormat: e.target.value as PaymentFormat })}
              >
                <option value="FULL">FULL (To'liq to'lov)</option>
                <option value="INSTALLMENT">INSTALLMENT (Bo'lib to'lash)</option>
              </select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-700">Mentor</Label>
              <select
                className="w-full h-10 px-3 rounded-xl border border-input bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                value={formData.mentorId}
                onChange={(e) => setFormData({ ...formData, mentorId: e.target.value })}
              >
                <option value="">Mentorni tanlang...</option>
                {mentors.map((m: any) => (
                  <option key={m.id} value={m.id}>
                    {m.fullName || m.name || m.title || m.id}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-700">Davomiyligi (soat/oy)</Label>
              <Input
                type="number"
                value={formData.duration}
                onChange={(e) => setFormData({ ...formData, duration: Number(e.target.value) })}
                className="rounded-xl bg-white"
                min={0}
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-700">Narxi (so'm)</Label>
              <Input
                type="number"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: Number(e.target.value) })}
                className="rounded-xl bg-white"
                disabled={formData.pricingType === 'FREE'}
                min={0}
              />
            </div>
          </div>

          {/* Rasmlar va Media */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-slate-50 p-4 rounded-xl border border-slate-100">
            <FileUploadInput
              label="Asosiy Rasm (Photo)"
              value={formData.photo}
              onChange={(url) => setFormData({ ...formData, photo: url })}
            />

            <FileUploadInput
              label="Ikonka (Icon)"
              value={formData.icon || ''}
              onChange={(url) => setFormData({ ...formData, icon: url })}
            />

            <FileUploadInput
              label="Karta Rasmi (Card Image)"
              value={formData.cardImage || ''}
              onChange={(url) => setFormData({ ...formData, cardImage: url })}
            />

            <FileUploadInput
              label="Banner Rasmi (Banner Image)"
              value={formData.bannerImage || ''}
              onChange={(url) => setFormData({ ...formData, bannerImage: url })}
            />
          </div>

          {/* Qo'shimcha sozlamalar (Switches) */}
          <div className="flex flex-wrap items-center gap-6 p-4 bg-slate-50 rounded-xl border border-slate-100">
            <div className="flex-1 min-w-[200px] rounded-xl border border-dashed border-slate-200 bg-white px-3 py-2">
              <p className="text-xs font-semibold text-slate-700">Presales (oldindan sotuv)</p>
              <p className="text-[11px] text-slate-500 mt-0.5">
                Faollashtirish: <strong>Presales</strong> bo&apos;limida kampaniya yarating (kurs holati avtomatik PRE_SALES).
                {courseToEdit?.presalesEnabled ? ' Hozir: yoqilgan.' : ' Hozir: o\'chirilgan.'}
              </p>
            </div>

            <div className="flex items-center gap-3">
              <Switch
                checked={formData.waitlistEnabled}
                onCheckedChange={(checked) => setFormData({ ...formData, waitlistEnabled: checked })}
              />
              <Label className="text-xs font-semibold text-slate-700 cursor-pointer">
                Kutish ro'yxati (Waitlist) faol
              </Label>
            </div>
          </div>

          {/* Tillarga oid ma'lumotlar (Translations) */}
          <div className="pt-2">
            <Label className="text-sm font-bold text-slate-900 mb-3 block">
              Ko'p tilli ma'lumotlar (UZ / RU / EN)
            </Label>
            <Tabs defaultValue="uz" className="w-full">
              <TabsList className="grid grid-cols-3 bg-slate-100 p-1 rounded-xl">
                <TabsTrigger value="uz" className="rounded-lg text-xs font-semibold">O'zbekcha (UZ)</TabsTrigger>
                <TabsTrigger value="ru" className="rounded-lg text-xs font-semibold">Русский (RU)</TabsTrigger>
                <TabsTrigger value="en" className="rounded-lg text-xs font-semibold">English (EN)</TabsTrigger>
              </TabsList>

              {(['uz', 'ru', 'en'] as const).map((locale) => {
                const translation = formData.translations.find((t) => t.locale === locale) || {
                  locale,
                  name: '',
                  title: '',
                  subtitle: '',
                  description: '',
                  courseOutcomes: '',
                  direction: '',
                  profession: '',
                };

                return (
                  <TabsContent key={locale} value={locale} className="space-y-4 pt-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <Label className="text-xs text-slate-600">Kurs Nomi (Name)</Label>
                        <Input
                          value={translation.name}
                          onChange={(e) => handleTranslationChange(locale, 'name', e.target.value)}
                          placeholder="Node.js Back-end"
                          className="rounded-xl"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs text-slate-600">Sarlavha (Title)</Label>
                        <Input
                          value={translation.title}
                          onChange={(e) => handleTranslationChange(locale, 'title', e.target.value)}
                          placeholder="Node.js orqali professional dasturlash"
                          className="rounded-xl"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs text-slate-600">Kichik Sarlavha (Subtitle)</Label>
                        <Input
                          value={translation.subtitle || ''}
                          onChange={(e) => handleTranslationChange(locale, 'subtitle', e.target.value)}
                          placeholder="Noldan professional darajagacha"
                          className="rounded-xl"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs text-slate-600">Yo'nalish (Direction)</Label>
                        <Input
                          value={translation.direction || ''}
                          onChange={(e) => handleTranslationChange(locale, 'direction', e.target.value)}
                          placeholder="Dasturlash / Backend"
                          className="rounded-xl"
                        />
                      </div>
                      <div className="space-y-1.5 md:col-span-2">
                        <Label className="text-xs text-slate-600">Kasb (Profession)</Label>
                        <Input
                          value={translation.profession || ''}
                          onChange={(e) => handleTranslationChange(locale, 'profession', e.target.value)}
                          placeholder="Backend Engineer"
                          className="rounded-xl"
                        />
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-xs text-slate-600">Kurs Tavsifi (Description)</Label>
                      <MarkdownEditor
                        value={translation.description}
                        onChange={(v) => handleTranslationChange(locale, 'description', v)}
                        placeholder="Kurs haqida to'liq ma'lumot..."
                        minHeight={90}
                      />
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-xs text-slate-600">Kurs Natijalari (Course Outcomes)</Label>
                      <MarkdownEditor
                        value={translation.courseOutcomes || ''}
                        onChange={(v) => handleTranslationChange(locale, 'courseOutcomes', v)}
                        placeholder="Talaba kursni tugatib nimalarni o'rganadi..."
                        minHeight={80}
                      />
                    </div>
                  </TabsContent>
                );
              })}
            </Tabs>
          </div>

          <DialogFooter className="pt-4 border-t border-slate-100">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-xl">
              Bekor qilish
            </Button>
            <Button type="submit" disabled={isCreating || isUpdating} className="rounded-xl bg-primary text-white">
              {isCreating || isUpdating ? 'Saqlanmoqda...' : 'Saqlash'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>

      <AlertDialog
        isOpen={publishConfirmOpen}
        setIsOpen={setPublishConfirmOpen}
        alertTitle="Kursni chop etish (PUBLISHED)?"
        alertDescription="PRE_SALES davridagi WAITING enrollmentlar ACTIVE ga o'tkaziladi, kutish ro'yxatidagi foydalanuvchilarga xabar yuboriladi, presales va waitlist flaglari o'chiriladi."
        alertCancel="Bekor qilish"
        alertActionTitle="Chop etish"
        alertActionFunction={() => {
          if (pendingPayload) submitUpdate(pendingPayload);
          setPublishConfirmOpen(false);
          setPendingPayload(null);
        }}
      />
    </Dialog>
  );
};
